/**
 * CSV Export Utility
 *
 * Generates RFC 4180-compliant CSV files and triggers browser downloads.
 * Used by the Storage Inventory feature for exporting filtered asset data.
 */

/**
 * Escapes a field value for RFC 4180 CSV encoding.
 * If the value contains a comma, double quote, or newline, it is wrapped in
 * double quotes. Internal double quotes are escaped by doubling them.
 */
export function escapeCSVField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const str = String(value);

  // If the field contains a comma, double quote, or newline, wrap in quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    // Escape internal double quotes by doubling them
    const escaped = str.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  return str;
}

/**
 * Generates a full CSV string with a header row followed by data rows.
 * Each row is terminated with CRLF as per RFC 4180.
 */
export function generateCSVContent(data: Record<string, unknown>[], columns: string[]): string {
  // Header row
  const headerRow = columns.map((col) => escapeCSVField(col)).join(',');

  // Data rows
  const dataRows = data.map((row) => {
    return columns.map((col) => escapeCSVField(row[col])).join(',');
  });

  // Join with CRLF line endings per RFC 4180
  return [headerRow, ...dataRows].join('\r\n');
}

/**
 * Creates a Blob from CSV content, generates an object URL, triggers a
 * browser file download via a hidden anchor element, then cleans up.
 */
export function exportToCSV(
  data: Record<string, unknown>[],
  columns: string[],
  filename: string
): void {
  const csvContent = generateCSVContent(data, columns);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Returns the export filename for a given category using the current date.
 * Format: storage-inventory-{category}-{YYYY-MM-DD}.csv
 */
export function getExportFilename(category: string): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  return `storage-inventory-${category}-${dateStr}.csv`;
}
